import { graphqlQuery } from '@/lib/api/graphql';
import {
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  computePaginationWithCount,
  createDefaultPagination,
  type GraphQLConnection,
  type PaginationResult,
  pageToCursor,
} from '@/lib/graphql-pagination';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { Transaction, TransactionStatus, TransactionType } from '@/types/transaction';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- GraphQL node type for case transactions ---

interface CaseTransactionGraphQLNode {
  caseId: number;
  caseTransactionId: number;
  transactionId: number;
  referenceNumber: string | null;
  customerId: number;
  customerName: string | null;
  accountNumber: string | null;
  transactionDate: string;
  dueDate: string | null;
  anticipatedDate: string | null;
  amount: number;
  netAmount: number | null;
  vatAmount: number | null;
  outstandingAmount: number;
  description: string | null;
  transactionNotes: string | null;
  headerReference: string | null;
  accountCode: string | null;
  onDispute: boolean;
  hasDispute: boolean;
  subLedger: string | null;
  subLedgerName: string | null;
  orderNumber: string | null;
  documentUrl: string | null;
  transactionReference: string | null;
  bankReference: string | null;
  currencyAmount: number | null;
  currencyNetAmount: number | null;
  currencyVatAmount: number | null;
  currencyOutstandingAmount: number | null;
  displayAmount: number | null;
  displayNetAmount: number | null;
  displayVatAmount: number | null;
  displayOutstandingAmount: number | null;
  daysOverdue: number;
  transactionCreatedAt: string;
  transactionModifiedAt: string | null;
  currencyCode: string | null;
  transactionTypeCode: string | null;
  transactionTypeName: string | null;
  transactionStatusCode: string | null;
  transactionStatusName: string | null;
  paymentMethodName: string | null;
  createdByName: string | null;
  modifiedByName: string | null;
  ageingBucket: string | null;
  totalAllocated: number;
  allocationCount: number;
  allocationRef: string | null;
  allocationStatusCode: string | null;
  allocationStatusName: string | null;
  hasPromise: boolean;
  hasCase: boolean;
  externalId: string | null;
}

interface CaseTransactionListGraphQLResponse {
  caseTransactions: {
    caseTransactionList: GraphQLConnection<CaseTransactionGraphQLNode>;
  };
}

interface CaseTransactionCountGraphQLResponse {
  caseTransactions: {
    caseTransactionCount: number;
  };
}

// --- GraphQL queries ---

const CASE_TRANSACTION_LIST_QUERY = `
  query CaseTransactionList($first: Int, $after: String, $sort: [CaseTransactionListSortSortByInput!], $filter: CaseTransactionListFilterInput) {
    caseTransactions {
      caseTransactionList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            caseId
            caseTransactionId
            transactionId
            referenceNumber
            customerId
            customerName
            accountNumber
            transactionDate
            dueDate
            anticipatedDate
            amount
            netAmount
            vatAmount
            outstandingAmount
            description
            transactionNotes
            headerReference
            accountCode
            onDispute
            hasDispute
            subLedger
            subLedgerName
            orderNumber
            documentUrl
            transactionReference
            bankReference
            currencyAmount
            currencyNetAmount
            currencyVatAmount
            currencyOutstandingAmount
            displayAmount
            displayNetAmount
            displayVatAmount
            displayOutstandingAmount
            daysOverdue
            transactionCreatedAt
            transactionModifiedAt
            currencyCode
            transactionTypeCode
            transactionTypeName
            transactionStatusCode
            transactionStatusName
            paymentMethodName
            createdByName
            modifiedByName
            ageingBucket
            totalAllocated
            allocationCount
            allocationRef
            allocationStatusCode
            allocationStatusName
            hasPromise
            hasCase
            externalId
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const CASE_TRANSACTION_COUNT_QUERY = `
  query CaseTransactionCount($filter: CaseTransactionListFilterInput) {
    caseTransactions {
      caseTransactionCount(filter: $filter)
    }
  }
`;

// --- Sort field map ---

const caseSortFieldMap: Record<string, string> = {
  referenceNumber: 'ReferenceNumber',
  transactionDate: 'TransactionDate',
  dueDate: 'DueDate',
  anticipatedDate: 'AnticipatedDate',
  customerName: 'CustomerName',
  transactionType: 'TransactionTypeName',
  amount: 'Amount',
  outstandingAmount: 'OutstandingAmount',
  status: 'TransactionStatusCode',
  currency: 'CurrencyCode',
  description: 'Description',
  createdBy: 'CreatedByName',
  modifiedBy: 'ModifiedByName',
  accountCode: 'AccountCode',
  netAmount: 'NetAmount',
  vatAmount: 'VatAmount',
  onDispute: 'OnDispute',
  subLedger: 'SubLedger',
  orderNumber: 'OrderNumber',
  currencyGross: 'CurrencyAmount',
  currencyNet: 'CurrencyNetAmount',
  currencyVAT: 'CurrencyVatAmount',
  currencyOutstanding: 'CurrencyOutstandingAmount',
};

// --- Mapping ---

function mapTransactionTypeCode(code: string | null): TransactionType {
  const typeMap: Record<string, TransactionType> = {
    invoice: 'invoice',
    credit_note: 'credit_note',
    receipt: 'receipt',
    debit_adjustment: 'debit_adjustment',
    credit_adjustment: 'credit_adjustment',
  };
  return typeMap[(code ?? '').toLowerCase()] ?? 'invoice';
}

function mapTransactionStatusCode(code: string | null): TransactionStatus {
  const statusMap: Record<string, TransactionStatus> = {
    open: 'outstanding',
    paid: 'completed',
    overdue: 'overdue',
  };
  return statusMap[(code ?? '').toLowerCase()] ?? 'outstanding';
}

function mapGraphQLNodeToTransaction(node: CaseTransactionGraphQLNode): Transaction {
  return {
    id: node.transactionId,
    referenceNumber: node.referenceNumber || String(node.transactionId),
    transactionDate: node.transactionDate,
    customerId: node.customerId,
    customerName: node.customerName ?? '',
    transactionType: mapTransactionTypeCode(node.transactionTypeCode),
    amount: node.amount,
    currency: node.currencyCode ?? 'GBP',
    status: mapTransactionStatusCode(node.transactionStatusCode),
    description: node.description ?? '',
    notes: node.transactionNotes ?? undefined,
    createdBy: node.createdByName ?? '',
    modifiedBy: node.modifiedByName ?? undefined,
    createdAt: node.transactionCreatedAt,
    modifiedAt: node.transactionModifiedAt ?? undefined,
    dueDate: node.dueDate ?? undefined,
    anticipatedDate: node.anticipatedDate ?? undefined,
    headerReference: node.headerReference ?? undefined,
    paymentMethod: node.paymentMethodName ?? undefined,
    documentUrl: node.documentUrl ?? undefined,
    outstandingAmount: node.outstandingAmount,
    onDispute: node.onDispute,
    hasDispute: node.hasDispute,
    accountCode: node.accountCode ?? undefined,
    netAmount: node.netAmount ?? undefined,
    vatAmount: node.vatAmount ?? undefined,
    subLedger: node.subLedger ?? undefined,
    subLedgerName: node.subLedgerName ?? undefined,
    orderNumber: node.orderNumber ?? undefined,
    transactionReference: node.transactionReference ?? undefined,
    bankReference: node.bankReference ?? undefined,
    currencyGross: node.currencyAmount ?? undefined,
    currencyNet: node.currencyNetAmount ?? undefined,
    currencyVAT: node.currencyVatAmount ?? undefined,
    currencyOutstanding: node.currencyOutstandingAmount ?? undefined,
    displayAmount: node.displayAmount ?? undefined,
    displayNetAmount: node.displayNetAmount ?? undefined,
    displayVatAmount: node.displayVatAmount ?? undefined,
    displayOutstandingAmount: node.displayOutstandingAmount ?? undefined,
    accountNumber: node.accountNumber ?? undefined,
    daysOverdue: node.daysOverdue,
    ageingBucket: node.ageingBucket ?? '',
    hasPromise: node.hasPromise,
    hasCase: node.hasCase,
    allocationStatusCode: node.allocationStatusCode ?? '',
    allocationStatusName: node.allocationStatusName ?? '',
    totalAllocated: node.totalAllocated,
    allocationCount: node.allocationCount,
    allocationRef: node.allocationRef ?? undefined,
    externalId: node.externalId ?? undefined,
  };
}

// --- Filter builder ---

export interface GetCaseTransactionsParams {
  caseId?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function buildCaseTransactionFilter(
  caseId: string | undefined,
  params?: GetCaseTransactionsParams,
): Record<string, unknown> | undefined {
  const filter: Record<string, unknown> = {};

  if (caseId) {
    filter.caseId = { eq: Number(caseId) };
  }

  if (params?.search) {
    filter.summary = params.search;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Result type ---

export interface CaseTransactionsGraphQLResult {
  transactions: Transaction[];
  pagination: PaginationResult;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// --- Hook ---

const defaultPagination: PaginationResult = createDefaultPagination(25);

interface CaseTransactionsListResult {
  transactions: Transaction[];
  pagination: PaginationResult;
}

export function useCaseTransactionsPaginatedGraphQL(
  caseId: string | undefined,
  params?: GetCaseTransactionsParams,
): CaseTransactionsGraphQLResult {
  const apiConfig = useApiConfig();

  const queryParams: GetCaseTransactionsParams = {
    page: 1,
    pageSize: 25,
    sortBy: 'transactionDate',
    sortOrder: 'desc',
    ...params,
  };

  const page = queryParams.page ?? 1;
  const pageSize = queryParams.pageSize ?? 25;

  const mockListResult: CaseTransactionsListResult = {
    transactions: [],
    pagination: computeMockPagination(0, page, pageSize),
  };

  const filter = buildCaseTransactionFilter(caseId, queryParams);

  // --- List query ---
  const listQuery = useModeAwareQuery<CaseTransactionsListResult, Error>(
    ['case-transactions-graphql-list', caseId, queryParams],
    async ({ signal }) => {
      if (!caseId) {
        return { transactions: [], pagination: computeMockPagination(0, 1, pageSize) };
      }

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildSort(caseSortFieldMap, queryParams.sortBy, queryParams.sortOrder),
        filter,
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<CaseTransactionListGraphQLResponse>(
        apiConfig,
        CASE_TRANSACTION_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.caseTransactions.caseTransactionList;
      const transactions = connection.edges.map((x) => mapGraphQLNodeToTransaction(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: transactions.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { transactions, pagination };
    },
    mockListResult,
    {
      enabled: !!caseId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  // --- Count query ---
  const countQuery = useModeAwareQuery<number, Error>(
    ['case-transactions-graphql-count', caseId, filter],
    async ({ signal }) => {
      if (!caseId) return 0;

      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CaseTransactionCountGraphQLResponse>(
        apiConfig,
        CASE_TRANSACTION_COUNT_QUERY,
        variables,
        signal,
      );

      return response.caseTransactions.caseTransactionCount;
    },
    0,
    {
      enabled: !!caseId,
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );

  // --- Combine results ---
  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    transactions: listQuery.data?.transactions ?? [],
    pagination,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
    },
  };
}

// --- Export filter builder (for useExportAll) ---

export function buildCaseTransactionExportFilter(
  caseId: string | undefined,
  search?: string,
): Record<string, unknown> | undefined {
  const filter: Record<string, unknown> = {};

  if (caseId) {
    filter.caseId = { eq: Number(caseId) };
  }

  if (search) {
    filter.summary = search;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Column name map for export ---

export const CASE_TRANSACTION_EXPORT_COLUMN_MAP: Record<string, string> = {
  referenceNumber: 'ReferenceNumber',
  transactionDate: 'TransactionDate',
  dueDate: 'DueDate',
  anticipatedDate: 'AnticipatedDate',
  transactionType: 'TransactionTypeName',
  amount: 'Amount',
  currency: 'CurrencyCode',
  outstandingAmount: 'OutstandingAmount',
  status: 'TransactionStatusName',
  description: 'Description',
  accountCode: 'AccountCode',
  netAmount: 'NetAmount',
  vatAmount: 'VatAmount',
  subLedger: 'SubLedger',
  orderNumber: 'OrderNumber',
  createdBy: 'CreatedByName',
  modifiedBy: 'ModifiedByName',
  customerName: 'CustomerName',
  onDispute: 'OnDispute',
  flags: 'Flags',
};
